import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { Modal, ModalTitle, ModalContent, Alert } from 'src/components/core';
import { Cross } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';

interface FuturesFundTradeInstructionModalProps {
    open: boolean;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { typography, colors, isMobile, breakpoints } }) => `
        .modal-paper {
            padding: ${isMobile ? '16px' : '24px'};
            @media (min-width: ${breakpoints.md}) {
                width: 480px;
            }
        }
        .modal-title {
            padding: 0px;
            > div {
                font-size: ${
                    isMobile
                        ? typography.size.mobile.heading.h6
                        : typography.size.desktop.heading.h6
                };
                line-height: ${isMobile ? typography.lineHeight[7] : typography.lineHeight[11]};
            }
        }
        .modal-content {
            padding: 32px 0 0 0;
            font-size: ${
                isMobile
                    ? typography.size.mobile.paragraph.p2
                    : typography.size.desktop.paragraph.p2
            };
            line-height: ${isMobile ? typography.lineHeight[3] : typography.lineHeight[6]};
            color: ${colors.text.primary};
        }
    `}
`;

const TitleText = styled.div`
    ${({ color }) => `
        color: ${color};
    `}
`;

const InstructionContainer = styled(FlexContainer)`
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding-bottom: 24px;
`;

const Instruction = styled(FlexContainer)`
    flex-direction: column;
    align-items: flex-start;
    ${({ theme: { typography, colors } }) => `
        & > div {
            font-weight: ${typography.weight.medium};
        }
        & > span {
            padding-left: 12px;
            color: ${colors.text.secondary};
        }
    `}
`;

const TermConditionContainer = styled(FlexContainer)`
    flex-direction: column;
    align-items: flex-start;
    ${({ theme: { typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: ${typography.lineHeight[4]};
        & > ul {
            padding-left: 23px;
            & > li {
                list-style: disc;
            }
        }
    `}
`;

const FuturesFundTradeInstructionModal: FC<FuturesFundTradeInstructionModalProps> = ({
    open,
    handleClose,
}) => {
    const { t } = useTranslation();
    const { colors } = useTheme();

    const instructionPoints = t('futures_bonus_trade_instruction_modal_points', {
        returnObjects: true,
    });
    const instructionPointsArray = Array.isArray(instructionPoints)
        ? instructionPoints
        : Object.values(instructionPoints);

    const termAndCondition = t('futures_bonus_trade_instruction_modal_term_condition', {
        returnObjects: true,
    });
    const termAndConditionArray = Array.isArray(termAndCondition['points'])
        ? termAndCondition['points']
        : Object.values(termAndCondition['points']);

    return (
        <StyledModal size="md" open={open}>
            <ModalTitle>
                <TitleText>{t('futures_bonus_trade_instruction_modal_title')}</TitleText>
                <Cross
                    fill={colors.text.primary}
                    onClick={handleClose}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                <InstructionContainer>
                    {instructionPointsArray.map((instruction, index) => (
                        <Instruction key={index}>
                            <TitleText>{`${index + 1}. ${t(instruction?.title)}`}</TitleText>
                            <span>{t(instruction.description)}</span>
                        </Instruction>
                    ))}
                </InstructionContainer>
                <Alert
                    showIcon={false}
                    content={
                        <TermConditionContainer>
                            <TitleText>{t(termAndCondition['title'])}</TitleText>
                            <ul>
                                {termAndConditionArray.map((condition, index) => (
                                    <li key={index}>{t(condition)}</li>
                                ))}
                            </ul>
                        </TermConditionContainer>
                    }
                    type={'warning'}
                />
            </ModalContent>
        </StyledModal>
    );
};

export { FuturesFundTradeInstructionModal };
